import Textarea from "@/components/form/Textarea";
import { ui } from "@/components/ui";
import { supabaseClient } from "@/lib/supabase";
import type { ActionKeyT } from "@/types/database/actions";
import type { StateKeyT, StatefulDiaryT } from "@/types/database/diaries";
import { useAuth } from "@/utils/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { camelCase, capitalCase } from "change-case";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import SaveButton from "../../../../../components/form/SaveButton";
import { useSelectActionsWordsQuery } from "../api/selectActionWords";
import { useSelectEmailSignaturesQuery } from "../api/selectEmailSignature";
import { useSelectStateWordsQuery } from "../api/selectStateWords";
import { buildEmail } from "../services/useEmailBuilder";

const formSchema = z.object({
	emailSignature: z.string().optional(),
	emailSalutation: z.string().optional(),
	actionWords: z.string().optional(),
	stateWords: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
	diaryKey: StateKeyT;
	actionKey: ActionKeyT;
	onEmailUpdate?: (body: string) => void;
	diary: StatefulDiaryT;
}

export function EmailWordsForm({
	diaryKey,
	actionKey,
	onEmailUpdate,
	diary,
}: Props) {
	const { id: userId } = useAuth();
	const queryClient = useQueryClient();

	const { data: emailConfig } = useSelectEmailSignaturesQuery(userId);
	const { data: actionWords } = useSelectActionsWordsQuery(actionKey, userId);
	const { data: stateWords } = useSelectStateWordsQuery(diaryKey, userId);

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			emailSignature: emailConfig?.email_signature ?? "",
			emailSalutation: emailConfig?.email_salutation ?? "Dear",
			actionWords: actionWords ?? "",
			stateWords: stateWords ?? "",
		},
	});

	const { mutate } = useMutation({
		mutationFn: async (values: FormValues) => {
			const updates = [];

			if (
				values.emailSignature !== undefined ||
				values.emailSalutation !== undefined
			) {
				updates.push(
					supabaseClient.from("email_configuration").upsert({
						user_id: userId,
						email_signature: values.emailSignature,
						email_salutation: values.emailSalutation,
					}),
				);
			}

			if (values.actionWords !== undefined) {
				updates.push(
					supabaseClient.from("action_words").upsert({
						user_id: userId,
						action_key: actionKey,
						action_words: values.actionWords,
					}),
				);
			}

			if (values.stateWords !== undefined) {
				updates.push(
					supabaseClient.from("state_words").upsert({
						user_id: userId,
						state_key: diaryKey,
						state_words: values.stateWords,
					}),
				);
			}

			await Promise.all(updates);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["email-signatures", userId] });
			queryClient.invalidateQueries({
				queryKey: ["actions-words", actionKey, userId],
			});
			queryClient.invalidateQueries({
				queryKey: ["state-words", diaryKey, userId],
			});
			queryClient.invalidateQueries({
				queryKey: ["email-builder", diaryKey, actionKey, userId],
			});

			// Build the new email body
			const newBody = buildEmail({
				toName: diary.patient_info?.patient_name ?? "Patient",
				start: form.getValues("stateWords") ?? "",
				end: form.getValues("actionWords") ?? "",
				from: form.getValues("emailSignature") ?? "",
				salutation: form.getValues("emailSalutation") ?? "Dear",
			});

			// Call the callback with the new body
			onEmailUpdate?.(newBody);

			toast.success("Successfully updated email words");
		},
		onError: (error) => {
			toast.error("Failed to update email words");
			console.error(error);
		},
	});

	function onSubmit(values: FormValues) {
		mutate(values);
	}

	return (
		<ui.Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
				<div className="space-y-2">
					<h3 className="font-medium text-lg">Email Words Configuration</h3>
					<div className="flex items-center gap-2 text-muted-foreground text-sm">
						<span>Configure email words for:</span>
						<ui.Badge variant="secondary">
							{capitalCase(camelCase(diaryKey))}
						</ui.Badge>
						<span>and</span>
						<ui.Badge variant="secondary">
							{capitalCase(camelCase(actionKey))}
						</ui.Badge>
					</div>
				</div>

				<div className="space-y-2">
					<div className="flex items-center gap-2">
						<ui.Input
							placeholder="Enter salutation..."
							{...form.register("emailSalutation")}
							className="w-32"
						/>
						<ui.Input
							value={`${diary.patient_info?.patient_name ?? "Patient"},`}
							disabled={true}
							className="w-full disabled:text-slate-900"
						/>
					</div>
				</div>

				<Textarea
					className="h-40"
					name="stateWords"
					placeholder="Enter state words..."
				/>

				<Textarea
					className="h-40"
					name="actionWords"
					placeholder="Enter action words..."
				/>

				<Textarea
					name="emailSignature"
					placeholder="Enter your email signature..."
				/>

				<SaveButton isDialog={true} />
			</form>
		</ui.Form>
	);
}
